/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FC, useCallback, useRef } from "react";
import { FC_ANCHOR } from "../FloatingContainer";
import { Icon, ICON_SIZE } from "../Icon";
import { Tooltip } from "../Tooltip";
import { FieldWrapper } from "./FieldWrapper";

export enum FIELD_BUTTON_FLAG {
	DYNAMIC = "dynamic"
}

export enum FIELD_BUTTON_STYLE {
	DEFAULT = "default",
	TRANSPARENT = "transparent"
}

export interface IFieldButtonProps {
	icon?: string;
	label?: string;
	tooltip?: string;
	disabled?: boolean;
	isActive?: boolean;
	style?: FIELD_BUTTON_STYLE;
	flag?: FIELD_BUTTON_FLAG;
	className?: string;
	grow?: boolean;
	onClick?: () => void;
	readOnly?: boolean;
}

export const FieldButton: FC<IFieldButtonProps> = ({
	icon,
	label,
	tooltip,
	disabled,
	isActive,
	style,
	flag,
	className,
	grow,
	onClick,
	readOnly
}) => {
	const controlElRef = useRef<HTMLButtonElement>();

	const handleButtonClick = useCallback(() => {
		if (!readOnly && onClick) {
			onClick();
		}
	}, [ onClick, readOnly ]);

	const baseClass = BEM("edt-field", "button", {
		"with-icon": icon ? true : false,
		"with-label": label ? true : false,
		"is-active": isActive,
		style: style,
		flag: flag,
		readonly: readOnly
	});

	baseClass.add("edt-field-control");

	return (
		<FieldWrapper type="button" grow={grow} className={className}>
			<button
				className={baseClass.toClassName()}
				ref={controlElRef}
				disabled={disabled || readOnly}
				onClick={handleButtonClick}
			>
				{icon ? <Icon name={icon} size={ICON_SIZE.XSMALL} /> : null}
				{label ? (
					<span className={baseClass.element("button-label").toClassName()}>{label}</span>
				) : null}
				{tooltip ? (
					<Tooltip for={controlElRef} anchor={FC_ANCHOR.CENTER_TOP} openDelay={1200}>
						{tooltip}
					</Tooltip>
				) : null}
			</button>
		</FieldWrapper>
	);
};
